/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {PackagesCartItemVoucher} from "types/entities/PackagesCartItemVoucher";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useVoucherTypeByArticle} from "utils/voucherUtils";
import BookingSellerReservationDetailVoucherActions
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationDetailVoucherActions";
import {Reservation} from "types/entities/Reservation";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import TableHeadColumn from "pages/Front/components/Table/TableHeadColumn";
import TableHeadTitle from "pages/Front/components/Table/TableHeadTitle";
import {BanknotesIcon, Cog6ToothIcon, PlusIcon, RectangleStackIcon} from "@heroicons/react/24/outline";
import Table from "pages/Front/components/Table/Table";
import TableHead from "pages/Front/components/Table/TableHead";
import {QrCodeIcon} from "@heroicons/react/24/solid";
import TableBody from "pages/Front/components/Table/TableBody";
import TableRow from "pages/Front/components/Table/TableRow";
import TableColumn from "pages/Front/components/Table/TableColumn";
import {closeModal, openModal} from "helpers/actions";
import {ModalSize} from "pages/Front/components/Popup";
import BookingSellerNewVoucherModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerNewVoucherModal";
import {useDispatch} from "useDispatch";

interface BookingSellerPackageCartItemVouchersTableProps {
    packagesCartItemVouchers: PackagesCartItemVoucher[];
    reservation: Reservation;
}

const BookingSellerPackageCartItemVouchersTable = (props: BookingSellerPackageCartItemVouchersTableProps) => {
    const {t} = useTranslation();
    const {getVoucherType} = useVoucherTypeByArticle();
    const dispatch = useDispatch();
    const onClickAddVoucher = () => {
        dispatch(
            openModal(<BookingSellerNewVoucherModal reservation={props.reservation} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            })
        );
    };

    return (
        <BookingCard className={clsx("ReservationsForDate !px-0 !py-0")}>
            <h2
                className={clsx(
                    "BookingCardTitle  font-bold text-xl text-black  text-center sm:text-left  px-3 xl:px-3 py-2.5 flex items-center justify-between "
                )}
            >
                <div className={"inline-flex items-center gap-x-2"}>
                    <span>{t("Vouchers")}</span>
                </div>
                <button
                    className={"bg-blue-400 hover:bg-blue-500 px-2 py-1 text-white rounded text-base flex flex-row items-center"}
                    onClick={onClickAddVoucher}
                >
                    <PlusIcon className={"w-4 h-4 mr-1.5"} />
                    <span>{t("Add voucher")}</span>
                </button>
            </h2>

            <Table className={"border-t"} responsive={true}>
                <colgroup className={"hidden lg:table-column-group"}>
                    <col className="min-w-[300px] w-auto" />
                    <col className="w-[90px]" />
                    <col className="w-auto" />
                    <col className="w-auto" />
                </colgroup>
                <TableHead>
                    <TableHeadColumn>
                        <TableHeadTitle>
                            <QrCodeIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Code")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle>
                            <Cog6ToothIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Actions")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-end"}>
                            <RectangleStackIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Type")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-end"}>
                            <BanknotesIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Price")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                </TableHead>
                <TableBody>
                    {props.packagesCartItemVouchers.length > 0 ? (
                        props.packagesCartItemVouchers.map((packagesCartItemVoucher, index) => (
                            <TableRow className={clsx("even:bg-gray-50 border-b border-b-gray-200/50 last:border-b-0")} key={index}>
                                <TableColumn>{packagesCartItemVoucher.voucher.campaign_name}</TableColumn>
                                <TableColumn>
                                    <BookingSellerReservationDetailVoucherActions
                                        packagesCartItemVoucher={packagesCartItemVoucher}
                                        reservation={props.reservation}
                                    />
                                </TableColumn>
                                <TableColumn className={"text-right"}>{getVoucherType(packagesCartItemVoucher.voucher).label}</TableColumn>
                                <TableColumn className={"text-right"}>- {getVoucherType(packagesCartItemVoucher.voucher).valueFormatted}</TableColumn>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <td className={"px-3 py-2 text-center"} colSpan={4}>
                                {t("No items")}
                            </td>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </BookingCard>
    );
};

export default BookingSellerPackageCartItemVouchersTable;
